import { motion } from "framer-motion";
import { ShieldCheck, FileCheck2 } from "lucide-react";

const steps = [
  {
    n: "01",
    title: "Keşif",
    body:
      "Sahaya geliyoruz; mevcut durumu, kısıtları ve hedeflerinizi birlikte değerlendiriyoruz. Ölçüm, fotoğraflama ve teknik ön rapor.",
  },
  {
    n: "02",
    title: "Malzeme Seçimi",
    body:
      "Bütçeniz ve kullanım amacınıza uygun malzeme alternatifleri sunuyoruz; numune, marka ve performans karşılaştırması.",
  },
  {
    n: "03",
    title: "Uygulama Planlaması",
    body:
      "Uygulama sırası, ekip planı ve iş takvimini çıkarıyoruz. Günlük yaşamınızı mümkün olduğunca az etkileyecek bir program hazırlıyoruz.",
  },
  {
    n: "04",
    title: "Şantiye Takibi",
    body:
      "Saha sorumlumuz işin başında. Sigortalı ekiplerimizle çalışıyor; İSG uygulamalarını, günlük raporlamayı, kalite kontrolü ve çalışma kapsamına uygun gerekli evrakları eksiksiz sağlıyoruz.",
  },
  {
    n: "05",
    title: "Teslim",
    body:
      "Temizlik ve son kontrollerin ardından yapılan işleri birlikte inceliyoruz. Evinizi iş teslim tutanağı ve garanti formuyla güvenli, kullanışlı ve yaşamaya hazır biçimde teslim ediyoruz.",
  },
];

export const Process = () => {
  return (
    <section
      id="surec"
      data-testid="process-section"
      className="py-24 sm:py-32 bg-zinc-50"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-zinc-500 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-zinc-950" />
              03 — Süreç
            </div>
            <h2 className="font-display font-black text-zinc-950 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Beş adımda{" "}
              <span className="italic font-medium text-zinc-400">
                öngörülebilir
              </span>{" "}
              bir şantiye.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base text-zinc-600 leading-relaxed">
              Sürprizleri azaltmak için süreci başından netleştiriyoruz. Her
              fazda sorumluluklar, malzemeler ve zaman çerçevesi yazılı.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 relative">
            <div className="absolute left-[34px] top-2 bottom-2 w-px bg-zinc-300" />
            <ul className="space-y-12">
              {steps.map((s, i) => (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`process-step-${i}`}
                  className="relative pl-24"
                >
                  <div className="absolute left-0 top-0 w-[70px] h-[70px] bg-white border border-zinc-200 flex items-center justify-center font-display font-black text-xl text-zinc-950">
                    {s.n}
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl text-zinc-950 tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-base text-zinc-600 leading-relaxed max-w-xl">
                    {s.body}
                  </p>
                </motion.li>
              ))}
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 lg:sticky lg:top-28 self-start"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-zinc-200">
              <img
                src="/assets/surec-insansiz-asamalar.webp"
                alt="Ölçüm, malzeme seçimi, yüzey hazırlığı ve tamamlanmış yaşam alanını gösteren tadilat süreci"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-zinc-950/10" />
              <div className="absolute bottom-1 sm:bottom-2 left-0 right-0 flex justify-center px-5">
                <img
                  src="/assets/artibir-logo.png"
                  alt="Artı Bir Yapı"
                  className="h-10 sm:h-12 w-auto max-w-[290px] object-contain"
                />
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
              <div className="p-5 sm:p-6 bg-[#00844A] border border-[#00844A] text-white">
                <ShieldCheck size={28} strokeWidth={1.75} className="mb-8" />
                <div className="text-[11px] mb-2 tracking-[0.18em] uppercase font-bold text-white/70">
                  Güvenli Uygulama
                </div>
                <div className="font-display font-black text-lg leading-tight">
                  Sigortalı Ekip & İSG
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/80">
                  Çalışma kapsamına uygun gerekli saha evrakları sağlanır.
                </p>
              </div>
              <div className="p-5 sm:p-6 bg-zinc-950 border border-zinc-950 text-white">
                <FileCheck2 size={28} strokeWidth={1.75} className="mb-8 text-[#00A85A]" />
                <div className="text-[11px] mb-2 tracking-[0.18em] uppercase font-bold text-zinc-500">
                  Belgeli Teslim
                </div>
                <div className="font-display font-black text-lg leading-tight">
                  Tutanak & Garanti Formu
                </div>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  İşçilik ve malzeme kapsamı yazılı olarak teslim edilir.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
